<template>

    <div class="page">

      <!-- KtDatatable Implementation -->
      <kt-datatable
          :model="pmModel"
          :data="rows"
          :filter="datatableFilter"
          :meta="{ tableClass: 'table-bordered', rowClass: (row) => getRowClass(row) }"
          :update-function="handleDatatableUpdate"
          :hide-search="true"
          storage-key="pm-list"
      >
        <template #view_actions="scope">
          <kt-dropmenu v-model:visible="actionDropdown['row_' + scope.row.Id]">
            <template #button>
              <button
                  class="btn btn-sm"
                  @click="actionDropdown['row_' + scope.row.Id] = !actionDropdown['row_' + scope.row.Id]"
              >
                <span><i class="fas fa-ellipsis-v"></i></span>
              </button>
            </template>
            <template #dropmenu>
              <ul class="dropdown-menu show">
                <li>
                  <label class="dropdown-item">
                    <button class="btn btn-sm btn-outline-primary form-control" @click="editRecord(scope.row.Id)">
                      <i class="fa fa-edit"></i> Edit
                    </button>
                  </label>
                </li>
                <li>
                  <label class="dropdown-item">
                    <button class="btn btn-sm btn-outline-danger form-control" @click="deleteRecord(scope.row.Id)">
                      <i class="fa fa-trash"></i> Remove
                    </button>
                  </label>
                </li>
              </ul>
            </template>
          </kt-dropmenu>
        </template>
        
        <template #filters="scope">
           <div class="col">
            <div class="row">
                <div class="col-auto">
                    <client-multi-check 
                        label="Clients"
                        :model-value="currentFilter.client"
                        @update:model-value="(value) => setFilter({client: value})"
                    ></client-multi-check>
                </div>
                <div class="col-auto">
                    <tech-multi-check 
                        label="Technicians"
                        :model-value="currentFilter.technician"
                        @update:model-value="(value) => setFilter({technician: value})"
                    ></tech-multi-check>
                </div>
                <div class="col-auto">
                    <kt-date-range-select
                        label="Due Date Range"
                        v-model="dateRange"
                    ></kt-date-range-select>
                </div>
                <div class="col-auto">
                    <label class="d-block text-muted small">Active Only</label>
                    <input 
                        type="checkbox" 
                        class="form-check-input" 
                        v-model="currentFilter.isActive"
                        @change="setFilter({isActive: currentFilter.isActive})"
                    />
                </div>
            </div>
           </div>
        </template>

        <template #after-search="scope">
            <div class="btn-group">
          <button @click="refresh()" 
              class="btn btn-sm btn-outline-secondary"
          >Refresh <i class="fa fa-sync"></i></button>

          <button @click="newRecord()" 
              class="btn btn-sm btn-outline-primary"
          >New PM <i class="fa fa-plus"></i></button>
          </div>
        </template>

        <template #view_string="scope">
          <span v-if="scope.column.name === 'Contact'">
              {{ scope.row.Contact?.Company || 'N/A' }}
          </span>
          <span v-else-if="scope.column.name === 'Technician'">
            {{ getTechnicianName(scope.row.AssignedTechnician) || 'N/A' }}
          </span>
          <span v-else-if="scope.column.name === 'NextDueDate'">
            {{ formatDate(scope.value) }}
          </span>
          <span v-else-if="scope.column.name === 'IsActive'">
            <span :class="scope.value ? 'badge bg-success' : 'badge bg-secondary'">
              {{ scope.value ? 'Active' : 'Inactive' }}
            </span>
          </span>
          <span v-else-if="scope.column.name === 'PMStatus'">
            <span :class="getStatusBadgeClass(scope.value)" class="badge">
              {{ getStatusName(scope.value) || 'N/A' }}
            </span>
          </span>
          <span v-else>
              {{ scope.value }}
          </span>
        </template>

        <!-- Filter badges in under-header slot -->
        <template #under-header>
          <div v-if="hasActiveFilters" class="d-flex flex-wrap gap-2">
            <span class="text-muted small me-2">Active filters:</span>
            
            <!-- Client badges -->
            <span v-for="clientId in currentFilter.client" :key="'client-' + clientId" 
                  class="badge bg-primary d-flex align-items-center">
              Client: {{ getClientName(clientId) }}
              <button type="button" class="btn-close btn-close-white ms-2" 
                      @click="removeClientFilter(clientId)"
                      style="font-size: 0.7em;"></button>
            </span>
            
            <!-- Technician badges -->
            <span v-for="techId in currentFilter.technician" :key="'tech-' + techId"
                  class="badge bg-success d-flex align-items-center">
              Technician: {{ getTechnicianName(techId) }}
              <button type="button" class="btn-close btn-close-white ms-2" 
                      @click="removeTechnicianFilter(techId)"
                      style="font-size: 0.7em;"></button>
            </span>
            
            <!-- Date Range badge -->
            <span v-if="currentFilter.startDate || currentFilter.endDate"
                  class="badge bg-info d-flex align-items-center">
              Due Date: {{ formatDateRange(currentFilter.startDate, currentFilter.endDate) }}
              <button type="button" class="btn-close btn-close-white ms-2" 
                      @click="clearDateRange()"
                      style="font-size: 0.7em;"></button>
            </span>

            <!-- Active Only badge -->
            <span v-if="currentFilter.isActive"
                  class="badge bg-warning text-dark d-flex align-items-center">
              Active Only
              <button type="button" class="btn-close ms-2" 
                      @click="setFilter({isActive: false})"
                      style="font-size: 0.7em;"></button>
            </span>
            
            <!-- Clear all button -->
            <button type="button" class="btn btn-sm btn-outline-secondary" 
                    @click="clearAllFilters()">
              Clear All Filters
            </button>
          </div>
        </template>

      </kt-datatable>

        <router-view name="modal"></router-view>

    </div>
    
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from "vue";
import { useRouter, useRoute, RouterView } from 'vue-router';
import { storeToRefs } from "pinia";
import Helper from "@/services/Helper";
import Api from "@/services/ApiService";
import Alert from "@/services/Alert";
import { PMModel, PMModelFilter } from "@/models/PMModel";
import KtDatatable from "@/components/UI/KtDatatable.vue";
import KtDropmenu from "@/components/UI/KtDropmenu.vue";
import { KtDatatableModelProp } from "@/components/UI/KtDataTable";

// stores
import { useClientStore } from '@/stores/ClientStore';
import { useTechStore } from "@/stores/TechStore";
import ClientMultiCheck from "@/components/Clients/ClientMultiCheck.vue";
import TechMultiCheck from "@/components/Techs/TechMultiCheck.vue";
import KtDateRangeSelect from "@/components/UI/KtDateRangeSelect.vue";

const techStore = useTechStore();
const { list: TechList } = storeToRefs(techStore);

const clientStore = useClientStore();
const { list: ClientList } = storeToRefs(clientStore);

// reload stores
techStore.reload();
clientStore.reload();

const router = useRouter();
const route = useRoute();

const total = ref(0);
const rows = ref<PMModel[]>([]);

// Filter state interface
interface PMListFilter {
    client: number[];
    technician: number[];
    isActive: boolean;
    startDate: string;
    endDate: string;
    page: number;
    size: number;
    sort: Array<Record<string, 'ASC' | 'DESC'>>;
}

// Current filter state (reactive)
const currentFilter = ref<PMListFilter>({
    client: [],
    technician: [],
    isActive: true,
    startDate: '',
    endDate: '',
    page: 0,
    size: 25,
    sort: []
});

// Datatable filter for pagination
const datatableFilter = computed(() => ({
    page: currentFilter.value.page,
    size: currentFilter.value.size,
    total: total.value,
    search: '',
    sort: currentFilter.value.sort
}));

// Date range picker
const dateRange = computed({
    get: () => ({
        startDate: currentFilter.value.startDate || null,
        endDate: currentFilter.value.endDate || null
    }),
    set: (value) => {
        setFilter({
            startDate: value.startDate || '',
            endDate: value.endDate || ''
        });
    }
});

const actionDropdown = ref<Record<string, boolean>>({});

// ===== URL & FILTER UTILITIES =====

// Parse sort parameter from URL
const parseSortFromUrl = (sortParam?: string): Array<Record<string, 'ASC' | 'DESC'>> => {
    if (!sortParam) return [];
    
    return sortParam.split(',').map(sortField => {
        const trimmed = sortField.trim();
        if (trimmed.startsWith('-')) {
            const column = trimmed.substring(1);
            return { [column]: 'DESC' as const };
        } else {
            return { [trimmed]: 'ASC' as const };
        }
    });
};

// Build URL search params from current filter
const buildUrlParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    
    if (currentFilter.value.client.length > 0) {
        params.set('client', currentFilter.value.client.join(','));
    }
    
    if (currentFilter.value.technician.length > 0) {
        params.set('technician', currentFilter.value.technician.join(','));
    }
    
    if (currentFilter.value.isActive) {
        params.set('isActive', '1');
    }
    
    if (currentFilter.value.startDate) {
        params.set('startDate', currentFilter.value.startDate);
    }
    
    if (currentFilter.value.endDate) {
        params.set('endDate', currentFilter.value.endDate);
    }
    
    if (currentFilter.value.page > 0) {
        params.set('page', currentFilter.value.page.toString());
    }
    
    if (currentFilter.value.sort.length > 0) {
        const sortStr = currentFilter.value.sort.map(sortObj => {
            const [key, direction] = Object.entries(sortObj)[0];
            return direction === 'DESC' ? `-${key}` : key;
        }).join(',');
        params.set('sort', sortStr);
    }
    
    return params;
};

// Load filter from URL on mount
const loadFilterFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    
    const client = urlParams.get('client')?.split(',').map(Number).filter(n => !isNaN(n)) || [];
    const technician = urlParams.get('technician')?.split(',').map(Number).filter(n => !isNaN(n)) || [];
    const isActive = urlParams.get('isActive') === '1';
    const startDate = urlParams.get('startDate') || '';
    const endDate = urlParams.get('endDate') || '';
    const page = parseInt(urlParams.get('page') || '0') || 0;
    const sortParam = urlParams.get('sort');
    const sort = parseSortFromUrl(sortParam);
    
    currentFilter.value = {
        client,
        technician,
        isActive,
        startDate,
        endDate,
        page,
        size: currentFilter.value.size,
        sort
    };
};

// Update URL when filter changes
const updateUrl = () => {
    const params = buildUrlParams();
    const newUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
    window.history.pushState({}, '', newUrl);
};

// Set filter and trigger load
const setFilter = (updates: Partial<PMListFilter>) => {
    // Reset page to 0 when changing filters (except when explicitly updating page)
    if (!('page' in updates)) {
        currentFilter.value.page = 0;
    }
    
    Object.assign(currentFilter.value, updates);
    updateUrl();
    load();
};

// ===== DATA LOADING =====

const load = async () => {
    const filter = new PMModelFilter();
    filter.CustomerNumber = currentFilter.value.client;
    filter.AssignedTechnician = currentFilter.value.technician;
    filter.IsActive = currentFilter.value.isActive ? true : null;
    filter.DueDateStart = currentFilter.value.startDate ? new Date(currentFilter.value.startDate) : null;
    filter.DueDateEnd = currentFilter.value.endDate ? new Date(currentFilter.value.endDate) : null;
    filter.Page = currentFilter.value.page;
    filter.Size = currentFilter.value.size;
    
    const response = await Api.loadPMs(filter);
    rows.value = response.Data || [];
    total.value = response.Meta?.Total || 0;
};

const refresh = () => {
    load();
};

// Handle datatable updates (pagination, sorting)
const handleDatatableUpdate = (updates: any) => {
    if ('page' in updates) {
        setFilter({ page: updates.page });
    }
    if ('size' in updates) {
        setFilter({ size: updates.size });
    }
    if ('sort' in updates) {
        setFilter({ sort: updates.sort });
    }
};

// ===== CRUD OPERATIONS =====

const newRecord = () => {
    router.push({ path: '/pm/new' });
};

const editRecord = (id: number) => {
    router.push({ path: `/pm/edit/${id}` });
};

const deleteRecord = async (id: number) => {
    Alert.confirm("Are you sure you want to delete this PM?", async () => {
        await Api.deletePM(id);
        refresh();
    });
};

// ===== DISPLAY HELPERS =====

const getClientName = (clientId: number) => {
    return ClientList.value[clientId]?.Company || `Client #${clientId}`;
};

const getTechnicianName = (techId: number) => {
    return TechList.value[techId]?.TechnicianName || 'N/A';
};

const getStatusName = (statusId: number) => {
    // You can create a PM status store similar to JobStatus if needed
    return `Status ${statusId}`;
};

const getStatusBadgeClass = (statusId: number) => {
    // Customize based on your PM status values
    return 'bg-info';
};

const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString();
};

const formatDateRange = (start: string, end: string) => {
    if (start && end) {
        return `${formatDate(start)} - ${formatDate(end)}`;
    } else if (start) {
        return `After ${formatDate(start)}`;
    } else if (end) {
        return `Before ${formatDate(end)}`;
    }
    return '';
};

const getRowClass = (row: PMModel) => {
    if (!row.IsActive) return 'table-secondary';
    if (row.NextDueDateAsDate && row.NextDueDateAsDate < new Date()) return 'table-warning';
    return '';
};

// ===== FILTER MANAGEMENT =====

const hasActiveFilters = computed(() => {
    return currentFilter.value.client.length > 0 ||
           currentFilter.value.technician.length > 0 ||
           currentFilter.value.startDate !== '' ||
           currentFilter.value.endDate !== '' ||
           currentFilter.value.isActive;
});

const removeClientFilter = (clientId: number) => {
    const newClients = currentFilter.value.client.filter(id => id !== clientId);
    setFilter({ client: newClients });
};

const removeTechnicianFilter = (techId: number) => {
    const newTechs = currentFilter.value.technician.filter(id => id !== techId);
    setFilter({ technician: newTechs });
};

const clearDateRange = () => {
    setFilter({ startDate: '', endDate: '' });
};

const clearAllFilters = () => {
    currentFilter.value = {
        client: [],
        technician: [],
        isActive: false,
        startDate: '',
        endDate: '',
        page: 0,
        size: currentFilter.value.size,
        sort: []
    };
    updateUrl();
    load();
};

// ===== DATATABLE MODEL =====

const pmModel = [
    {
        name: "Actions",
        label: "Actions",
        type: "actions"
    },
    {
        name: "Id",
        label: "ID",
        display: true,
        sortable: true
    },
    {
        name: "Title",
        label: "PM Title",
        display: true,
        type: "string"
    },
    {
        name: "Contact",
        label: "Client",
        display: true,
        type: "string"
    },
    {
        name: "Technician",
        label: "Assigned To",
        display: true,
        type: "string"
    },
    {
        name: "FrequencyType",
        label: "Frequency",
        display: true,
        sortable: true
    },
    {
        name: "NextDueDate",
        label: "Next Due",
        display: true,
        type: "date",
        sortable: true
    },
    {
        name: "IsActive",
        label: "Active",
        display: true,
        type: "string"
    },
    {
        name: "CompletionStatus",
        label: "Status",
        display: true,
        type: "string"
    }
];

// ===== LIFECYCLE =====

// Watch route params to reload data when returning from modal
watch(() => route.params.id, (newVal, oldVal) => {
    // If we had a param (modal was open) and now we don't (modal closed), reload
    if (oldVal && !newVal) {
        load();
    }
});

onMounted(() => {
    loadFilterFromUrl();
    load();
});

</script>

<style scoped>
.page {
    padding: 1rem;
}
</style>
